import { cloneDeep } from "lodash";
import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useImmer } from "use-immer";

import {
  ColorSuccess,
  IgButton,
  IgLink,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  CouponType,
  SubscriptionDto,
  SubscriptionInterval,
  SubscriptionVersionType,
} from "@intelligems/ig-types";
import { Button, ButtonGroup, Grid, Typography } from "@mui/material";
import { SxProps } from "@mui/system";
import { useIsMutating } from "@tanstack/react-query";

import { sortBy } from "../../api/formatters/onboarding";
import { useSubscriptions } from "../../api/organization-subscriptions/get-subscriptions";
import { useGetOrg } from "../../api/organizations/get-organization";
import { fetchCoupon } from "../../api/stripe/get-coupon";
import { useGetBillingUrl } from "../../api/subscriptions/get-billing-url";
import { useLogout } from "../../api/user-auths/logout";
import theme from "../../theme";
import { pricingPage } from "../../utils/linkSheet";
import openNewTab, { appUtmParams } from "../../utils/openNewTab";
import { SkeletonGroup } from "../Utils/SkeletonGroup";

import { SubscriptionPlanBox } from "./SubscriptionPlanBox";
import { createBundle, maybeAddOldPlans } from "./utils";

export type AvailableSubscriptionTypeFe = SubscriptionDto & {
  isSelected?: boolean;
};

interface SubscriptionPriceSectionProps {
  showHeader?: boolean;
  fullWidth?: boolean;
  onSetupLaterClick: () => void;
  selectedTestType?: string;
}

const CURRENT_VERSION: SubscriptionVersionType = "2024-02";

const SelectedButtonGroupSx: SxProps = {
  "&:hover, &:focus": {
    borderColor: ColorSuccess.main,
    backgroundColor: ColorSuccess.dark,
  },
  borderColor: ColorSuccess.main,
  backgroundColor: ColorSuccess.main,
  color: "white",
};

const UnSelectedButtonGroupSx: SxProps = {
  "&:hover, &:focus": {
    borderColor: ColorSuccess.main,
  },
  borderColor: ColorSuccess.main,
  color: ColorSuccess.main,
};

function getOrderPlanTypeFromOrder(orderCount: number): string[] {
  if (orderCount <= 2500) {
    return ["2K", "SAPPHIRE"];
  }
  if (orderCount <= 5000) {
    return ["5K", "RUBY"];
  }
  if (orderCount <= 10000) {
    return ["10K", "DIAMOND"];
  }
  if (orderCount <= 15000) {
    return ["15K", "DIAMOND"];
  }
  if (orderCount <= 20000) {
    return ["20K", "DIAMOND"];
  }
  if (orderCount <= 35000) {
    return ["35K", "PLUS"];
  }
  if (orderCount <= 50000) {
    return ["50K", "PLUS"];
  }
  if (orderCount <= 75000) {
    return ["75K", "PLUS"];
  }
  if (orderCount <= 100000) {
    return ["100K", "PLUS"];
  }
  if (orderCount <= 150000) {
    return ["150K", "PLUS"];
  }
  if (orderCount <= 200000) {
    return ["200K", "PLUS"];
  }
  return ["300K", "PLUS"];
}

export const SubscriptionPriceSection = ({
  showHeader,
  onSetupLaterClick,
  selectedTestType,
}: SubscriptionPriceSectionProps) => {
  const [subInterval, setSubInterval] = useImmer<
    SubscriptionInterval | undefined
  >(undefined);
  const { state } = useLocation();

  const [availableSubscriptions, setAvailableSubscriptions] = useImmer<
    AvailableSubscriptionTypeFe[]
  >([]);
  const [bundleAdded, setBundleAdded] = useImmer<boolean>(false);
  const [isBundleCurrent, setIsBundleCurrent] = useImmer<boolean>(false);
  const [code, setCode] = useImmer<string>("");
  const [fetchingCoupon, setFetchingCoupon] = useImmer<boolean>(false);
  const [coupon, setCoupon] = useImmer<CouponType | null>(null);
  const organization = useGetOrg();
  const subscriptions = useSubscriptions({ orgId: organization.data.org.id });
  const getBillingUrl = useGetBillingUrl();
  const isBillingMutating = useIsMutating({
    mutationKey: ["request-billing-url"],
  });
  const logout = useLogout();
  const orderPlan = getOrderPlanTypeFromOrder(
    organization.data?.org.orderCount || 0
  );

  const buttonText = useMemo(() => {
    if (subscriptions.isSuccess) {
      if (subscriptions.data.orgSubscriptions.length > 0) {
        return "Get Started";
      }
      if (!organization.data.org.freeTrialExpiresAtTs) {
        return "Start Trial";
      }
      const freeTrialEndDate = new Date(
        organization.data.org.freeTrialExpiresAtTs
      );
      const today = new Date();
      return freeTrialEndDate >= today ? "Start Trial" : "Get Started";
    }
    return "Start Trial";
  }, [organization.data.org.freeTrialExpiresAtTs, subscriptions]);

  useEffect(() => {
    if (selectedTestType) {
      const planTestName =
        selectedTestType === "content" || selectedTestType === "campaign"
          ? selectedTestType.toUpperCase()
          : "PRICING";
      setAvailableSubscriptions((draft) => {
        const index = draft.findIndex(
          (sub) =>
            sub.name.includes(planTestName) &&
            sub.defaultInterval === (subInterval || "monthly")
        );

        if (index != -1 && index <= availableSubscriptions.length - 1) {
          draft[index].isSelected = true;
        }
        return draft;
      });
    }
  }, [selectedTestType]);

  useEffect(() => {
    if (subscriptions.isSuccess && !bundleAdded) {
      const bundle = createBundle(
        subscriptions.data.orgSubscriptions,
        subscriptions.data.allSubscriptions
      );
      setIsBundleCurrent(!!bundle);

      let plans: AvailableSubscriptionTypeFe[] =
        subscriptions.data.allSubscriptions.filter((sub) =>
          ["CAMPAIGNS", "CONTENT", "PRICING", "ADDON", "PROFIT", "BLUE"].some(
            (name) => sub.name.includes(name) && sub.version === CURRENT_VERSION
          )
        );

      if (bundle) {
        plans.push(bundle);
      }

      const oldPlans: AvailableSubscriptionTypeFe[] =
        subscriptions.data.allSubscriptions.filter((sub) =>
          ["CAMPAIGNS", "CONTENT", "PRICING", "ADDON", "PROFIT", "BLUE"].some(
            (name) => sub.name.includes(name) && sub.version !== CURRENT_VERSION
          )
        );

      plans = maybeAddOldPlans(
        plans,
        oldPlans,
        subscriptions.data.orgSubscriptions
      );

      const addonSub = availableSubscriptions.find((sub) =>
        sub.name.includes("ADDON")
      );

      if (addonSub) {
        const addonIndex = plans.findIndex((sub) => sub.id === addonSub.id);
        if (addonIndex !== -1) {
          plans[addonIndex].isSelected = true;
        }
      }

      plans = cloneDeep(plans);
      for (const plan of plans) {
        if (
          subscriptions.data.orgSubscriptions.some(
            (sub) => sub.subscriptionId === plan.id
          )
        ) {
          plan.isSelected = true;
        }
      }

      setAvailableSubscriptions(plans);
      setBundleAdded(true);
    }
  }, [subscriptions.isSuccess && subscriptions.data]);

  useEffect(() => {
    const content = availableSubscriptions.find((sub) =>
      sub.name.includes("CONTENT")
    )?.isSelected;
    const campaigns = availableSubscriptions.find((sub) =>
      sub.name.includes("CAMPAIGNS")
    )?.isSelected;
    const pricing = availableSubscriptions.find((sub) =>
      sub.name.includes("PRICING")
    )?.isSelected;

    const bundleIndex = availableSubscriptions.findIndex(
      (sub) => sub.id === "bundle"
    )!;

    if (bundleIndex != -1) {
      if (
        availableSubscriptions[bundleIndex].isSelected ||
        (content && campaigns && pricing)
      ) {
        setAvailableSubscriptions((draft) => {
          draft[bundleIndex].isSelected = true;

          const shippingIndex = draft.findIndex((sub) =>
            sub.name.includes("SHIPPING")
          );
          if (shippingIndex !== -1) {
            draft[shippingIndex].isSelected = false;
          }

          const basicIndex = draft.findIndex((sub) =>
            sub.name.includes("BASIC")
          );
          if (basicIndex !== -1) {
            draft[basicIndex].isSelected = false;
          }

          return draft;
        });
      } else {
        setAvailableSubscriptions((draft) => {
          draft[bundleIndex].isSelected = false;
          return draft;
        });
      }
    }
  }, [availableSubscriptions]);

  const selectedSubscriptionIds = useMemo(
    () =>
      availableSubscriptions
        .filter((sub) => sub.id !== "bundle" && sub.isSelected)
        .map((sub) => sub.id)
        .filter((sub) => !!sub),
    [availableSubscriptions]
  );

  const onApproveClick = () => {
    getBillingUrl.mutate({
      body: {
        // @ts-ignore
        subscriptionIds: selectedSubscriptionIds,
        ...(coupon && {
          code,
        }),
      },
      pathParams: undefined,
      queryParams: undefined,
    });
  };

  const onApplyClick = async () => {
    setCoupon(null);
    setFetchingCoupon(true);
    const couponResp = await fetchCoupon(code);
    setFetchingCoupon(false);
    if (couponResp?.coupon) {
      setCoupon(couponResp.coupon);
    } else {
      setCoupon(null);
      toast.error(`Code: ${code} not valid`);
    }
  };

  const formatCouponMessage = (coupon: CouponType) => {
    const off = coupon.percentOff
      ? `${coupon.percentOff}%`
      : `$${(coupon.amountOff! / 100).toFixed(2)}`;
    return `'${coupon.name}' redeemed! You've unlocked ${off} off.`;
  };

  useEffect(() => {
    if (getBillingUrl.isSuccess) {
      onSetupLaterClick();
    }
  }, [getBillingUrl.isSuccess]);

  const availableSubsWithoutAddonCurrVersion = useMemo(() => {
    const subs = availableSubscriptions.filter((sub) => {
      if (sub.version === CURRENT_VERSION) {
        return (
          sub.defaultInterval === subInterval &&
          orderPlan.some((plan) => {
            if (plan.includes("K")) {
              return sub.name.split(";")[0] === plan;
            }
            return plan.includes(sub.name.split(";")[0]);
          }) &&
          [
            "CAMPAIGNS",
            "CONTENT",
            "PRICING",
            "SHIPPING",
            "BASIC",
            "PROFIT",
            "BLUE",
          ].some((name) => sub.name.includes(name) || sub.id === "bundle")
        );
      } else {
        return [
          "CAMPAIGNS",
          "CONTENT",
          "PRICING",
          "SHIPPING",
          "BASIC",
          "PROFIT",
          "BLUE",
        ].some((name) => sub.name.includes(name) || sub.id === "bundle");
      }
    });

    const final: any[] = [];
    for (const sub of subs) {
      if (sub.isSelected) {
        final.push(sub);
      }
    }

    for (const sub of subs) {
      if (
        !final.some(
          (s) => s.name.split(";").pop() === sub.name.split(";").pop()
        )
      ) {
        final.push(sub);
      }
    }

    return final;
  }, [availableSubscriptions, subInterval]);

  useEffect(() => {
    if (
      subscriptions.isSuccess &&
      subscriptions.data.orgSubscriptions !== undefined &&
      subInterval === undefined
    ) {
      setSubInterval(
        (subscriptions.data.orgSubscriptions || []).some(
          (sub) => sub.subscriptionInterval === "annual"
        )
          ? "annual"
          : "monthly"
      );
    }
  }, [subscriptions.isSuccess, subscriptions.data]);

  useEffect(() => {
    const el = document.getElementById(state?.targetId);
    if (el) {
      window.scrollTo({
        behavior: "smooth",
        top:
          el.getBoundingClientRect().top -
          document.body.getBoundingClientRect().top +
          25,
      });
    }
  }, [
    state?.targetId,
    subscriptions.isSuccess,
    availableSubscriptions,
    subInterval,
  ]);

  if (
    subscriptions.isSuccess &&
    subscriptions.data &&
    availableSubscriptions &&
    availableSubscriptions.length === 0
  ) {
    return null;
  }

  if (subInterval && availableSubscriptions.length && subscriptions.isSuccess) {
    return (
      <Grid
        container
        item
        direction={"column"}
        alignItems={"center"}
        justifyContent={"space-between"}
        spacing={2}
        id={"intelligems-plan"}
      >
        {showHeader ? (
          <>
            <Grid item xs id={"pricing-plan"} mt={1}>
              <Typography
                variant={"h5"}
                color={theme.palette.primary.main}
                fontWeight={600}
              >
                CUSTOMIZE YOUR PLAN
              </Typography>
            </Grid>
            <Grid item sx={{ textAlign: "center" }} mb={2}>
              <IgTypography>
                <IgTypography sx={{ fontWeight: 600 }}>
                  7-day free trial for new customers.
                </IgTypography>
                <br /> Based on your monthly orders you fit into the following
                plans.
                <br /> Detailed plan comparisons available{" "}
                <IgLink
                  onClick={() => {
                    openNewTab(pricingPage);
                  }}
                >
                  here
                </IgLink>
                .
              </IgTypography>
            </Grid>
          </>
        ) : null}
        <Grid item sx={{ marginTop: "-18px" }}>
          <ButtonGroup disableElevation={true}>
            <Button
              sx={
                subInterval === "monthly"
                  ? SelectedButtonGroupSx
                  : UnSelectedButtonGroupSx
              }
              onClick={() => setSubInterval("monthly")}
            >
              MONTHLY
            </Button>
            <Button
              sx={
                subInterval === "annual"
                  ? SelectedButtonGroupSx
                  : UnSelectedButtonGroupSx
              }
              onClick={() => setSubInterval("annual")}
            >
              ANNUAL
            </Button>
          </ButtonGroup>
        </Grid>
        <Grid item xs>
          <Grid
            container
            direction={"row"}
            spacing={1}
            justifyContent={"center"}
          >
            {cloneDeep(availableSubsWithoutAddonCurrVersion)
              .sort(sortBy)
              .map((plan) => (
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={3}
                  lg={
                    availableSubsWithoutAddonCurrVersion.length === 5
                      ? 2.4
                      : availableSubsWithoutAddonCurrVersion.length === 4
                        ? 3
                        : 4
                  }
                  key={`subscription-plan-${plan.id}-${plan.isCurrent}`}
                  style={{ marginBottom: "24px" }}
                >
                  <SubscriptionPlanBox
                    subscription={plan}
                    orgSubscriptions={subscriptions.data.orgSubscriptions}
                    addonSubscription={
                      plan.name.includes("PRICING")
                        ? availableSubscriptions.find((sub) =>
                            sub.name.includes("ADDON")
                          )
                        : undefined
                    }
                    buttonText={buttonText}
                    availableSubscriptions={availableSubscriptions}
                    setAvailableSubscriptions={setAvailableSubscriptions}
                    isBundleCurrent={isBundleCurrent}
                    coupon={coupon || undefined}
                  />
                </Grid>
              ))}
          </Grid>
        </Grid>
        {subscriptions.data.orgSubscriptions.length === 0 && (
          <Grid
            item
            container
            sx={{ paddingTop: "40px !important" }}
            columnSpacing={1}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Grid item>
              <IgTextField
                size={"small"}
                value={code}
                placeholder={"enter code here"}
                onChange={(e) => {
                  setCode(e.target.value?.toUpperCase());
                }}
                inputProps={{
                  style: {
                    width: "125px",
                  },
                }}
              ></IgTextField>
            </Grid>
            <Grid item>
              <IgButton
                igVariant={"gray"}
                variant={"outlined"}
                disabled={!code.length}
                loading={fetchingCoupon}
                onClick={() => void onApplyClick()}
                sx={{
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  paddingTop: "5px",
                  paddingBottom: "5px",
                  fontSize: "16px",
                }}
              >
                Apply Code
              </IgButton>
            </Grid>
          </Grid>
        )}
        <Grid item>
          {coupon && <IgTypography>{formatCouponMessage(coupon)}</IgTypography>}
        </Grid>
        <Grid item sx={{ paddingTop: "10px !important" }}>
          <IgButton
            variant={"contained"}
            disabled={selectedSubscriptionIds.length === 0}
            loading={!!isBillingMutating}
            onClick={() => onApproveClick()}
            sx={{
              paddingLeft: "39px",
              paddingRight: "39px",
              paddingTop: "10px",
              paddingBottom: "10px",
              fontSize: "16px",
            }}
          >
            Approve Now To {buttonText}
          </IgButton>
        </Grid>
        <Grid item sx={{ paddingTop: "10px !important" }}>
          <IgButton
            variant={"outlined"}
            loading={!!isBillingMutating}
            onClick={() =>
              window.open(
                `https://meetings.hubspot.com/igsam/igdemo${appUtmParams}`,
                "_blank"
              )
            }
            sx={{
              paddingLeft: "92px",
              paddingRight: "92px",
              paddingTop: "10px",
              paddingBottom: "10px",
              fontSize: "16px",
            }}
          >
            Book A Demo
          </IgButton>
        </Grid>
        {showHeader ? null : (
          <Grid item sx={{ paddingTop: "5px !important" }}>
            <IgButton
              onClick={() => logout.mutate()}
              sx={{
                paddingLeft: "50px",
                paddingRight: "50px",
                paddingTop: "0px",
                paddingBottom: "0px",
                fontSize: "16px",
              }}
            >
              Log Out
            </IgButton>
          </Grid>
        )}
      </Grid>
    );
  }
  return <SkeletonGroup lines={4} />;
};
